import axios from '@/plugins/axios'
import store from '@/store/RootState'
import moment from 'moment'
import get = Reflect.get;

function getRedirectUrl (): string {
  if (process.env.NODE_ENV === 'development') return 'http://localhost:8080/redirected'
  else return 'https://queued.no/redirected'
}

export default class APISpotify {
  static async getToken (privateToken = false, code?: string): Promise<string> {
    try {
      if (privateToken && code) {
        const res = await axios.post('/getPrivateToken', { redirectUri: getRedirectUrl(), code: code })
        store.commit('user/setPrivateToken', res.data.body.access_token)
        store.commit('user/setRefreshToken', res.data.body.refresh_token)
        store.commit('user/setTokenExpires', moment().add(1, 'hour').valueOf())
        return res.data.body.access_token as string
      } else if (store.state.user.tokenExpires > moment().add(5, 'minutes').valueOf()) {
        return store.state.user.privateToken
      } else if (store.state.user.refreshToken.length > 0) {
        const res = await axios.post('/refreshToken', { redirectUri: getRedirectUrl(), refreshToken: store.state.user.refreshToken })
        store.commit('user/setPrivateToken', res.data.body.access_token)
        store.commit('user/setTokenExpires', moment().add(1, 'hour').valueOf())
        return res.data.body.access_token as string
      } else {
        const token = await axios.get('/getNewToken')
        store.commit('user/setPrivateToken', token.data.body.access_token)
        store.commit('user/setTokenExpires', moment().add(1, 'hour').valueOf())
        return token.data.body.access_token
      }
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  static async getRedirectUrl (): Promise<string> {
    try {
      const res = await axios.post('/getAccessCodeUrl', { redirectUri: getRedirectUrl(), state: store.state.partyRoom.partyRoom?.partyID + '!' + store.state.user.user?.userID })
      return res.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  static async generalSearch (query: string): Promise<Record<string, any>> {
    const header = { Authorization: 'Bearer ' + await APISpotify.getToken() }
    try {
      const res = await axios.get('https://api.spotify.com/v1/search?q=' + query + '&type=artist,track,playlist&market=NO&limit=3', { headers: header })
      return res.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  static async getArtistTracks (artistID: string): Promise<Record<string, any>> {
    const header = { Authorization: 'Bearer ' + await APISpotify.getToken() }
    try {
      const res = await axios.get('https://api.spotify.com/v1/artists/' + artistID + '/top-tracks?country=NO', { headers: header })
      return res.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  static async getArtistAlbums (artistID: string): Promise<Record<string, any>> {
    const header = { Authorization: 'Bearer ' + await APISpotify.getToken() }
    try {
      const res = await axios.get('https://api.spotify.com/v1/artists/' + artistID + '/top-tracks', { headers: header })
      return res.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  static async getPlaylist (playlistID: string): Promise<Record<string, any>> {
    const header = { Authorization: 'Bearer ' + await APISpotify.getToken() }
    try {
      const res = await axios.get('https://api.spotify.com/v1/playlists/' + playlistID, { headers: header })
      return res.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  static async searchAllTracks (query: string): Promise<Record<string, any>> {
    const header = { Authorization: 'Bearer ' + await APISpotify.getToken() }
    try {
      const res = await axios.get('https://api.spotify.com/v1/search?q=' + query + '&type=track&market=NO', { headers: header })
      return res.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  static async searchAllPlaylists (query: string): Promise<Record<string, any>> {
    const header = { Authorization: 'Bearer ' + await APISpotify.getToken() }
    try {
      const res = await axios.get('https://api.spotify.com/v1/search?q=' + query + '&type=playlist&market=NO', { headers: header })
      return res.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  static async searchAllArtists (query: string): Promise<Record<string, any>> {
    const header = { Authorization: 'Bearer ' + await APISpotify.getToken() }
    try {
      const res = await axios.get('https://api.spotify.com/v1/search?q=' + query + '&type=artist&market=NO', { headers: header })
      return res.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  static async searchNext (url: string): Promise<Record<string, any>> {
    const header = { Authorization: 'Bearer ' + await APISpotify.getToken() }
    try {
      const res = await axios.get(url, { headers: header })
      return res.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  static async getTopCategories (): Promise<SpotifyApi.MultipleCategoriesResponse> {
    const header = { Authorization: 'Bearer ' + await APISpotify.getToken() }
    try {
      const res = await axios.get('https://api.spotify.com/v1/browse/categories?country=NO&locale=nb_NO', { headers: header })
      return res.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  static async getCategorysPlaylist (categoryID: string): Promise<SpotifyApi.CategoryPlaylistsReponse> {
    const header = { Authorization: 'Bearer ' + await APISpotify.getToken() }
    try {
      const res = await axios.get('https://api.spotify.com/v1/browse/categories/' + categoryID + '/playlists?country=NO&locale=nb_NO', { headers: header })
      return res.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  static async getSavedTracks (): Promise<SpotifyApi.UsersSavedTracksResponse> {
    const header = { Authorization: 'Bearer ' + await APISpotify.getToken() }
    try {
      const res = await axios.get('https://api.spotify.com/v1/me/tracks', { headers: header })
      return res.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  static async getSavedPlaylists (): Promise<SpotifyApi.ListOfUsersPlaylistsResponse> {
    const header = { Authorization: 'Bearer ' + await APISpotify.getToken() }
    try {
      const res = await axios.get('https://api.spotify.com/v1/me/playlists', { headers: header })
      return res.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }
}
